import { useNavigate } from 'react-router-dom';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { debounce } from 'lodash';
import { useQueryClient } from '@tanstack/react-query';

import { TagContextProvider } from 'lib/context/TagContext/TagContextProvider';

import ErrorBoundary from 'components/shared/Error/Boundary';

import TagTableView from './components/TagTableView';
import { updateTagLinks } from './lib/updateTagLinks';
import TagsReorder from './components/TagsReorder/TagsReorder';

/**
 * @param {import('./lib/propTypes').TagsProps} props
 */

const Tags = (props) => {
  const { value, data, query, refTable, kind, label, fromTable = false } = props;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isCreateModal, setIsCreateModal] = useState(false);
  const [openReorderModal, setOpenReorderModal] = useState(false);

  // Assigned in an effect (not during render) so the ref only ever holds committed values —
  // a concurrent render that React discards must not leak its params into the debounced call.
  const updateParamsRef = useRef();
  useEffect(() => {
    updateParamsRef.current = { navigate, queryClient, query, refTable, fromTable };
  });

  const debouncedUpdateTags = useMemo(
    () =>
      debounce((id, tags, mode) => {
        updateTagLinks({ id, tags, mode, ...updateParamsRef.current });
      }, 1000),
    []
  );

  useEffect(() => () => debouncedUpdateTags.flush(), [debouncedUpdateTags]);

  const handleAddTags = (data, tag) => {
    const filteredNewTags = tag?.filter(
      (newTag) => !data?.taglinks?.some((existingTag) => existingTag?.id === newTag?.id)
    );
    debouncedUpdateTags(data?.id, filteredNewTags, 'add');
  };

  const handleRemoveTag = async (data, id) => {
    await updateTagLinks({
      navigate,
      queryClient,
      id: data?.id,
      fromTable,
      tags: [id],
      mode: 'remove',
      query,
      refTable
    });
  };

  const tableActions = [
    {
      label: 'Create tag',
      icon: 'tag-new-mono',
      onClick: () => setIsCreateModal(true)
    },
    {
      label: 'Reorder tags',
      icon: 'arrow-swap-down-mono',
      onClick: () => setOpenReorderModal(true),
      disabled: value?.length < 2,
      tippyText:
        value?.length < 2 ? `Select tags to add to this ${label || kind}` : 'Reorder selected tags'
    },
    {
      label: 'Manage tags',
      icon: 'settings-mono',
      onClick: () => navigate('/portal/settings/tags'),
      disabled: false
    }
  ];

  return (
    <ErrorBoundary>
      <TagContextProvider>
        <TagTableView
          currTags={value || []}
          tagsKind={kind}
          handleAdd={(tag) => handleAddTags(data, tag)}
          handleRemove={(id) => handleRemoveTag(data, id)}
          tableActions={tableActions}
          isCreateModal={isCreateModal}
          setIsCreateModal={setIsCreateModal}
          usePendingState
          fromTable
          removeConfirmationTitle={`Are you sure you want to remove this tag from the ${label || kind}?`}
        />
        <TagsReorder
          openReorderModal={!!openReorderModal}
          handleClose={() => setOpenReorderModal(false)}
          refId={data?.id}
          refTable={refTable}
          fromTable
          kind={kind}
        />
      </TagContextProvider>
    </ErrorBoundary>
  );
};

export default Tags;
